<template>
  <el-dialog
    :title="getLabelText(base, 'create')"
    :visible.sync="visible"
    custom-class="rwd-dialog-large"
  >
    <el-form
      :model="formData"
      v-loading="loading"
      label-position="top"
      ref="formData"
    >
      <el-row :gutter="20">
        <el-col :xs="24" :md="24">
          <std-opt
            v-model="formData.invoice.type"
            :labelText="$t('api.soa_invoice.fields.invoice_type.label')"
            name="soaInvoiceType"
            :hiddenValue="[0]"
            component="radio-button"
          >
          </std-opt>
        </el-col>
        <el-col :xs="24" :md="24">
          <remote-opt
            v-model="formData.bid"
            :base="base"
            name="bid"
            :labelArr="['bid', 'unhide_bid', 'name']"
            :fetchList="fetchBranchList"
            :srchFilter="branchFilter"
            keyName="bid"
            valueName="bid"
            @change="onChangeBranch"
            sort="bid"
            required
          >
          </remote-opt>
        </el-col>
        <el-col :xs="24">
          <time-field
            v-model="formData.date"
            :base="base"
            name="date"
            prop="date"
            dataType="datetime"
            format="yyyy-MM-dd"
            valueFormat="yyyy-MM-dd"
            required
          >
          </time-field>
        </el-col>
        <el-col :xs="24" :md="12">
          <std-opt
            v-model="formData.due_date_type"
            name="documentDueDateType"
            component="radio-button"
            :hiddenValue="dueDateHiddenValue"
          >
          </std-opt>
        </el-col>
        <el-col :xs="24" :md="12">
          <text-field
            v-model="dueDate"
            :base="base"
            name="due_date"
            disabled
          >
          </text-field>
        </el-col>
        <el-col :xs="24"
          v-if="showDueDateTypeAlert"
        >
          <el-alert
            :title="dueDateTypeAlertTitle"
            :closable="false"
            type="warning"
            show-icon
          >
          </el-alert>
        </el-col>
        <el-col :xs="24" :md="12">
          <std-opt
            v-model="formData.vat_apply_type"
            name="documentVATApplyType"
            component="radio-button"
            @input="calcAmount"
          >
          </std-opt>
        </el-col>
        <el-col :xs="24"
          v-if="showVATApplyTypeAlert"
        >
          <el-alert
            :title="vatApplyTypeAlertTitle"
            :closable="false"
            type="warning"
            show-icon
          >
          </el-alert>
        </el-col>
        <el-col :xs="24">
          <document-item
            v-model="formData.item"
            :base="base"
            :currencyCode="currencyCode"
            :currencyPrecision="currencyPrecision"
            :isAddDeposit="formData.invoice.type === SOAInvoiceType.Deposit"
            prop="item"
            @change="calcAmount"
            ref="item"
          >
          </document-item>
        </el-col>
        <el-col :xs="24" :md="12">
          <text-field
            v-model="calcData.vat_amount"
            :base="base"
            name="vat_amount"
            disabled
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="12">
          <text-field
            v-model="calcData.total_amount"
            :base="base"
            name="total_amount"
            disabled
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="24">
          <text-field
            v-model="formData.subject"
            :base="base"
            name="subject"
            required
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="24">
          <text-field
            v-model="formData.remark"
            :base="base"
            name="remark"
            required
          >
          </text-field>
        </el-col>
        <el-col :xs="24" :md="24">
          <text-field
            v-model="formData.memo"
            :base="base"
            name="memo"
          >
          </text-field>
        </el-col>
      </el-row>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button
        @click="visible = false"
      >
        {{ $t('api.cancelBtn') }}
      </el-button>
      <el-button type="primary"
        @click="save"
        :loading="saveLoading"
      >
        {{ $t('api.saveBtn') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import SOA from '@/mixins/soa'
import { create } from '@/api/chart/soa/invoice'
import {
  fetchOne as fetchBranch,
  fetchList as fetchBranchList
} from '@/api/merchant/branch'
import { decimalFormat } from '@/utils/number'
import { getMap, getMapLabel, transText, getLabelText } from '@/utils/i18n'
import { branchFilter } from '@/utils/filter'
import {
  SOAInvoiceType,
  SOADocumentVATApplyType,
  SOADocumentDueDateType,
  BranchVATType
} from '@/enums'

const defFormData = {
  due_date_type: SOADocumentDueDateType.Immediate,
  date: null,
  bid: undefined,
  vat_apply_type: SOADocumentVATApplyType.SameAsBranch,
  item: [],
  subject: '',
  remark: '',
  memo: '',
  invoice: {
    type: SOAInvoiceType.Deposit
  }
}

const defCalcData = {
  vat_amount: 0,
  total_amount: 0
}

export default {
  name: 'invoice-dialog',
  mixins: [SOA],
  data () {
    return {
      base: 'soa_invoice',
      formData: Object.assign({}, defFormData),
      calcData: Object.assign({}, defCalcData),
      branchFilter: branchFilter,
      dueDate: '',
      saveLoading: false,
      loading: false,
      branchData: null,
      visible: false,
      SOAInvoiceType: SOAInvoiceType
    }
  },
  props: {
    afterSaveOK: {
      type: Function,
      default: () => {}
    }
  },
  computed: {
    showVATApplyTypeAlert () {
      return this.branchData &&
        this.formData.vat_apply_type === SOADocumentVATApplyType.SameAsBranch
    },
    vatApplyTypeAlertTitle () {
      if (this.branchData && this.branchData.payment) {
        let m = this.getMap('vatType')
        let label = this.getMapLabel(m, this.branchData.payment.vat_type)
        if (this.branchData.payment.vat_type === 0 && this.branchRegion) {
          label += `(${this.branchRegion.tax_adj_rate}%)`
        }

        return this.$t('api.optFields.documentVATApplyType.warning', {
          vat_type: label
        })
      }

      return ''
    },
    vatRate () {
      if (this.formData.vat_apply_type === SOADocumentVATApplyType.Exempt) {
        return 0
      }

      if (this.branchData && this.branchData.payment) {
        switch (this.branchData.payment.vat_type) {
          case BranchVATType.Registered:
            return (this.branchRegion) ? -this.branchRegion.tax_adj_rate : 0
          case BranchVATType.ZeroRate:
            return 0
          case BranchVATType.Exempt:
            return 0
        }
      }

      return 0
    },
    invoiceBaseFilter () {
      return '{bid:' + this.formData.bid + '}'
    },
    invoiceSrchFilter () {
      return '{$and:[{bid:' + this.formData.bid + '},{number:"%`value`%"}]}'
    }
  },
  methods: {
    create,
    fetchBranch,
    fetchBranchList,
    decimalFormat,
    getMap,
    getMapLabel,
    transText,
    getLabelText,
    reset () {
      this.formData = JSON.parse(JSON.stringify(defFormData))
      this.formData.date = this.$moment().format('YYYY-MM-DD')
      this.calcData = Object.assign({}, defCalcData)
      this.branchData = null
      if (this.$refs.item) {
        this.$nextTick(() => {
          this.$refs.item.reset()
        })
      }
    },
    show () {
      this.visible = true
    },
    hide () {
      this.visible = false
    },
    save () {
      this.saveLoading = true
      this.$refs.formData.validate(valid => {
        if (!valid) {
          this.saveLoading = false
          return
        }

        this.create(this.formData).then(() => {
          this.$message({
            message: this.transText(this.base + '.addSuccess'),
            type: 'success',
            duration: 2000
          })
          this.hide()
          this.reset()
          this.afterSaveOK()
        }).catch((err) => {
          console.log(err)
        }).then(() => {
          this.saveLoading = false
        })
      })
    },
    onChangeBranch (v) {
      this.formData.bid = v
      this.getBranchData(v)
    },
    getBranchData (bid) {
      this.loading = true
      this.fetchBranch(bid).then(res => {
        this.branchData = res.data
      }).catch((err) => {
        console.log(err)
      }).then(() => {
        this.loading = false
        this.$_calcDueDate()
        this.calcAmount()
      })
    },
    calcAmount () {
      let itemSum = this.$refs.item.sumAmount()

      this.calcData.vat_amount = this.decimalFormat(
        itemSum * (this.vatRate / 100),
        this.currencyPrecision
      )
      this.calcData.total_amount = this.decimalFormat(
        itemSum + this.calcData.vat_amount,
        this.currencyPrecision
      )
    }
  },
  created () {
    this.reset()
  },
  watch: {
    'formData.type' (v) {
      this.$_handleDueDateType(v)
    },
    'formData.date' (v) {
      this.$_calcDueDate()
    },
    'formData.due_date_type' (v) {
      this.$_calcDueDate()
    }
  }
}

</script>
